import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { isAxiosError } from 'axios'
import { Base, LockIcon, ReverLoading, toast } from '@itsrever/design-system'
import { LoadError, PageHeader, StoreOnly } from '@/ui/shared'
import { isHubApp } from '@/domain/app-catalog'
import { useWarehousesScope } from '@/hooks/use-warehouses-scope'
import { useAppSelector } from '@/redux/hooks'
import { useDialog } from '@/ui/components/dialog/dialog-provider'
import {
  GroupNangoConnection,
  IntegrationKind,
  CONNECTABLE_INTEGRATIONS,
  assignableIntegrationsOf,
  canConnectAgain,
  connectionLabel,
  integrationKindOf,
  isBroken
} from '@/domain/nango-connection'
import {
  useAssignNangoPlatformConnection,
  useDeleteNangoConnection,
  useGroupNangoConnections,
  useNangoConnect,
  useNangoReconnect
} from '@/application/manage-nango-connections'
import { DeleteCredentialDialog } from '../credentials/delete-credential-dialog'
import { AppAutomationTokenCard } from './app-automation-token-card'
import { PlatformPickerDialog } from './platform-picker-dialog'
import { IntegrationsList } from '../integrations-list'
import {
  ProviderLogo,
  providerName,
  providerOf,
  RAW_TEXT,
  useOtherStoreCopy,
  useStoppedCopy,
  useUsedBySummary
} from './hub-parts'

const {
  Badge,
  Button,
  Callout,
  Card,
  TabsRoot,
  TabsList,
  TabsIndicator,
  Tab,
  TabPanel
} = Base

// Apps, like Carriers: the catalog of apps REVER's team sets up per portal, and the group's Nango connections.
const EXPLORE_TAB = 'explore'
const INSTALLED_TAB = 'installed'

type ConnectionRef = Pick<GroupNangoConnection, 'integration' | 'connectionId'>

const toRef = ({ integration, connectionId }: ConnectionRef) => ({
  integration,
  connectionId
})

const sameConnection = (a: ConnectionRef, b: ConnectionRef) =>
  a.integration === b.integration && a.connectionId === b.connectionId

const connectionKey = (c: ConnectionRef) => `${c.integration}/${c.connectionId}`

// Envoy sends a refusal as a 400 whose body holds its gRPC code and message.
const GRPC_INVALID_ARGUMENT = 3
const GRPC_FAILED_PRECONDITION = 9

// settings-api leads a refusal of another store's connection with this, then names the portal's store.
const OTHER_STORE = 'other_store: '

// The store the portal uses, when settings-api refused a connection of another store.
function portalStoreOf(error: unknown) {
  const body = isAxiosError(error)
    ? (error.response?.data as { code?: unknown; message?: unknown })
    : undefined
  const message = String(body?.message ?? '')
  return body?.code === GRPC_FAILED_PRECONDITION &&
    message.startsWith(OTHER_STORE)
    ? message.slice(OTHER_STORE.length)
    : undefined
}

// Anything but a refusal may work on a retry.
function assignErrorKey(error: unknown) {
  const body = isAxiosError(error)
    ? (error.response?.data as { code?: unknown; message?: unknown })
    : undefined
  if (body?.code === GRPC_INVALID_ARGUMENT)
    return 'integrations_hub.platform.assign_no_store'
  if (body?.code !== GRPC_FAILED_PRECONDITION)
    return 'integrations_hub.use_error'
  // settings-api leads with Nango's code when the connection can't sign in to its store.
  return String(body.message ?? '').startsWith('invalid_credentials')
    ? 'integrations_hub.platform.assign_cant_sign_in'
    : 'integrations_hub.platform.assign_rejected'
}

export default function ConnectionsSection({
  kind
}: {
  kind: IntegrationKind
}) {
  const { t, i18n } = useTranslation()
  const copy = (key: string) => t(`integrations_hub.${kind}.${key}`)
  // Group or portal, as picked in the selector, like Configuration > Warehouses.
  const { scope, showGroup } = useWarehousesScope()
  const group = scope.kind === 'none' ? undefined : scope.group
  const groupStores = scope.kind === 'none' ? [] : scope.stores
  const store = scope.kind === 'store' ? scope.ecommerceId : undefined
  const [deleting, setDeleting] = useState<ConnectionRef>()
  const [picking, setPicking] = useState(false)
  const usedBy = useUsedBySummary()
  const stopped = useStoppedCopy()
  const otherStore = useOtherStoreCopy()
  const showDialog = useDialog()
  const storeSettings = useAppSelector((s) => s.settingsApi.settings.response)
  // The loaded settings are another portal's until the selected one's arrive: offer nothing meanwhile.
  const assignable =
    store && storeSettings.ecommerce_id === store
      ? assignableIntegrationsOf(storeSettings.platform)
      : []

  const {
    connections: all,
    loadingConnections,
    connectionsError,
    reloadConnections
  } = useGroupNangoConnections({ enabled: !!group, group })
  const connections = all.filter((c) => integrationKindOf(c) === kind)
  const connectable = CONNECTABLE_INTEGRATIONS[kind]
  const connect = useNangoConnect(group, () => reloadConnections(), connectable)
  const reconnect = useNangoReconnect(group, () => reloadConnections())
  const { deleteConnection, deleting: deletingConnection } =
    useDeleteNangoConnection()
  const { assignPlatformConnection, assigning } =
    useAssignNangoPlatformConnection()

  const assign = async (connection: GroupNangoConnection) => {
    if (!store) return
    const confirmed = await showDialog({
      title: t('integrations_hub.platform.assign_title', {
        name: providerName(connection),
        store
      }),
      message: t('integrations_hub.platform.assign_message', { store }),
      okButtonText: t('integrations_hub.platform.use_here')
    })
    if (!confirmed) return
    try {
      await assignPlatformConnection({
        ecommerceId: store,
        ...toRef(connection)
      })
    } catch (error) {
      const portalStore = portalStoreOf(error)
      // A Shopify store is its .myshopify.com name, which never moves: settings-api never switches it.
      if (portalStore && connection.integration !== 'shopify') {
        await switchStore(connection, portalStore)
        return
      }
      toast({ text: t(assignErrorKey(error)), variant: 'error' })
    }
  }

  // A shop on a new domain connects it as a new connection: the portal moves to it once the merchant confirms.
  const switchStore = async (
    connection: GroupNangoConnection,
    portalStore: string
  ) => {
    if (!store) return
    const values = {
      store,
      portalStore,
      connection: connectionLabel(connection),
      ...RAW_TEXT
    }
    const confirmed = await showDialog({
      title: t('integrations_hub.platform.switch_store_title', values),
      message: t('integrations_hub.platform.switch_store_message', values),
      okButtonText: t('integrations_hub.platform.switch_store')
    })
    if (!confirmed) return
    try {
      await assignPlatformConnection({
        ecommerceId: store,
        ...toRef(connection),
        switchStore: true
      })
    } catch (error) {
      toast({ text: t(assignErrorKey(error)), variant: 'error' })
    }
  }

  const confirmDelete = async () => {
    if (!group || !deleting) return
    try {
      await deleteConnection({ group, ...deleting })
      setDeleting(undefined)
    } catch {
      toast({ text: t('nango_credentials.delete_error'), variant: 'error' })
    }
  }

  if (scope.kind === 'none') {
    return (
      <>
        <PageHeader title={copy('title')} />
        <p className="px-7 py-6 text-sm text-neutral-600">
          {t('integrations_hub.no_scope')}
        </p>
      </>
    )
  }

  const dateOf = (c: GroupNangoConnection) =>
    c.createdAt?.toLocaleDateString(i18n.language)

  const connectedOn = (c: GroupNangoConnection) =>
    dateOf(c)
      ? t('integrations_hub.connected_on', { date: dateOf(c), ...RAW_TEXT })
      : ''

  const row = (
    connection: GroupNangoConnection,
    leading: React.ReactNode,
    details: string[],
    trailing: React.ReactNode
  ) => {
    const name = providerName(connection)
    return (
      <div
        key={connectionKey(connection)}
        className="flex items-center gap-4 border-b border-solid border-neutral-100 px-4 py-3 last:border-b-0"
      >
        {leading}
        <ProviderLogo provider={providerOf(connection)} name={name} />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <p className="flex items-center gap-2 text-sm font-medium text-neutral-900">
            {name}
            <span className="truncate font-normal text-neutral-500">
              {connectionLabel(connection)}
            </span>
            {isBroken(connection) ? (
              <Badge tone="danger" dot>
                {t('integrations_hub.attention.action_needed')}
              </Badge>
            ) : null}
          </p>
          {isBroken(connection) ? (
            <p className="text-xs text-red-700">
              {stopped(connection, { onRow: true })}
            </p>
          ) : null}
          {isBroken(connection) && otherStore(connection) ? (
            <p className="text-xs text-neutral-600">{otherStore(connection)}</p>
          ) : null}
          {isBroken(connection) && connection.health?.platformError ? (
            <p className="break-all text-xs text-neutral-600">
              {t('integrations_hub.attention.answer', {
                name,
                error: connection.health.platformError,
                ...RAW_TEXT
              })}
            </p>
          ) : null}
          {details.filter(Boolean).map((detail) => (
            <p key={detail} className="text-xs text-neutral-600">
              {detail}
            </p>
          ))}
        </div>
        {trailing}
      </div>
    )
  }

  const reconnectButton = (connection: GroupNangoConnection) => (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      aria-label={t('integrations_hub.attention.reconnect_aria', {
        name: providerName(connection),
        connection: connectionLabel(connection),
        ...RAW_TEXT
      })}
      onClick={() => reconnect(toRef(connection))}
    >
      {t('integrations_hub.attention.reconnect')}
    </Button>
  )

  const groupRow = (connection: GroupNangoConnection) => {
    const name = providerName(connection)
    const connectsAgain = canConnectAgain(connection)
    return row(
      connection,
      null,
      [
        usedBy(connection.linkedEcommerceIds, groupStores),
        connectedOn(connection)
      ],
      <div className="flex items-center gap-2">
        {/* Wherever Connect can create it again, so the merchant can also change keys that still work. */}
        {connectsAgain ? reconnectButton(connection) : null}
        {connectsAgain ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label={t('integrations_hub.disconnect_aria', {
              name,
              connection: connectionLabel(connection),
              ...RAW_TEXT
            })}
            onClick={() => setDeleting(toRef(connection))}
          >
            {t('integrations_hub.disconnect')}
          </Button>
        ) : null}
      </div>
    )
  }

  const storeRow = (connection: GroupNangoConnection) => {
    if (!store) return null
    const used = connection.linkedEcommerceIds.includes(store)
    const name = providerName(connection)
    // Only a working connection of the portal's own platform can replace the one it reads from.
    const canAssign =
      !used &&
      !isBroken(connection) &&
      assignable.includes(connection.integration)
    return row(
      connection,
      null,
      [connectedOn(connection)],
      // The connection is the group's and serves its other portals: reconnecting happens there.
      isBroken(connection) && canConnectAgain(connection) ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label={t('integrations_hub.attention.reconnect_in_aria', {
            name,
            connection: connectionLabel(connection),
            group,
            ...RAW_TEXT
          })}
          onClick={showGroup}
        >
          {t('integrations_hub.attention.reconnect_in', { group, ...RAW_TEXT })}
        </Button>
      ) : canAssign ? (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={assigning}
          aria-label={t('integrations_hub.use_here_aria', { name })}
          onClick={() => assign(connection)}
        >
          {t('integrations_hub.platform.use_here')}
        </Button>
      ) : (
        <span className="text-xs text-neutral-500">
          {t(used ? 'integrations_hub.used_here' : 'integrations_hub.not_used')}
        </span>
      )
    )
  }

  const list = connectionsError ? (
    /* An error must not read as "nothing connected" on a credentials page. */
    <LoadError
      message={t('integrations_hub.load_error')}
      onRetry={() => reloadConnections()}
    />
  ) : loadingConnections ? (
    <ReverLoading />
  ) : (
    <Card variant="flat" className="overflow-hidden">
      {connections.length === 0 ? (
        <p className="px-4 py-6 text-sm text-neutral-600">{copy('empty')}</p>
      ) : (
        connections.map(store ? storeRow : groupRow)
      )}
    </Card>
  )

  const installed = (
    <>
      {store ? (
        // The group view can't add or disconnect apps either, so there's nowhere to send them.
        kind === 'app' ? null : (
          <Callout
            tone="info"
            icon={<LockIcon />}
            className="text-xs leading-relaxed"
          >
            <Trans
              i18nKey="integrations_hub.store_banner"
              values={{ group, store }}
              shouldUnescape
              components={{
                b: <b />,
                action: (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    inline
                    onClick={showGroup}
                  />
                )
              }}
            />
          </Callout>
        )
      ) : (
        <div className="flex items-center gap-3">
          <p className="text-xs text-neutral-500">
            {t('integrations_hub.disclaimer')}
          </p>
          <p className="ml-auto text-xs text-neutral-600">
            {loadingConnections || connectionsError
              ? null
              : t('integrations_hub.count', { count: connections.length })}
          </p>
          {connectable.length > 0 ? (
            <Button
              type="button"
              variant="primary"
              onClick={() =>
                kind === 'platform' ? setPicking(true) : connect()
              }
            >
              {copy('connect')}
            </Button>
          ) : null}
        </div>
      )}
      {list}
      {store && kind === 'platform' && assignable.includes('shopify') ? (
        <AppAutomationTokenCard ecommerceId={store} />
      ) : null}
    </>
  )

  return (
    <>
      <PageHeader
        title={copy('title')}
        description={copy(store ? 'store_description' : 'description')}
        actions={
          store ? (
            <Badge tone="info">
              {t('warehouses.scope_store', { store, ...RAW_TEXT })}
            </Badge>
          ) : undefined
        }
      />
      {kind === 'app' ? (
        <div className="max-w-screen-xl px-7 py-4">
          <TabsRoot defaultValue={EXPLORE_TAB}>
            <TabsList>
              <Tab value={EXPLORE_TAB}>
                {t('integrations_store.explore_tab')}
              </Tab>
              <Tab value={INSTALLED_TAB}>
                {t('integrations_store.installed_tab')}
              </Tab>
              <TabsIndicator />
            </TabsList>
            <TabPanel value={EXPLORE_TAB}>
              <StoreOnly>
                <IntegrationsList
                  category="All"
                  searchQuery=""
                  filter={isHubApp}
                />
              </StoreOnly>
            </TabPanel>
            <TabPanel
              value={INSTALLED_TAB}
              className="flex flex-col gap-4 pt-4"
            >
              {installed}
            </TabPanel>
          </TabsRoot>
        </div>
      ) : (
        <div className="flex max-w-screen-xl flex-col gap-4 px-7 py-4">
          {installed}
        </div>
      )}

      <DeleteCredentialDialog
        connection={
          deleting ? all.find((c) => sameConnection(c, deleting)) : undefined
        }
        deleting={deletingConnection}
        onClose={() => setDeleting(undefined)}
        onDelete={confirmDelete}
      />
      <PlatformPickerDialog
        open={picking}
        onClose={() => setPicking(false)}
        onPick={(integration) => {
          setPicking(false)
          connect([integration])
        }}
      />
    </>
  )
}
